<template>
    <div v-if="isLoading" class="loader-wrapper">
        <UILoader />
    </div>

    <template v-else>
        <HeaderSection @logout="logOut" />

        <main class="main">
            <NavbarMenu />

            <div class="main__content">
                <BreadCrumbs />
                <BackButton v-if="hasBtnBack" />
                <UIContainer
                    :title="route.meta?.title?.value"
                    :name="route.meta?.name?.value"
                    :icon-name="route.meta?.iconName"
                    :sub-menu="route.meta?.subMenu"
                >
                    <router-view />
                </UIContainer>
            </div>
        </main>
    </template>

    <RootModals />
</template>

<script>
export default {
    name: 'DefaultLayout',
};
</script>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, provide, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { UserService } from '@/services/user-service';
import { ROUTE_NAMES } from '@/router/router-names';
import NavbarMenu from '@/components/NavbarMenu/index.vue';
import HeaderSection from '@/components/HeaderSection/index.vue';
import BreadCrumbs from '@/components/BreadCrumbs/index.vue';
import BackButton from '@/components/ui/buttons/BackButton';
import UIContainer from '@/components/UIContainer/index.vue';
import Translations from '@/i18n/translation';
import { CatalogService } from '@/services/catalog-service';
import { LocalizationService } from '@/services/localization-service';
import { changeLocaleCountries } from '@/composables/helpers';
import UILoader from '@/components/ui/UILoader';
import RootModals from '@/components/RootModals/index.vue';

const isLoading = ref(true);
const emitter = inject('emitter');
const route = useRoute();
const router = useRouter();
const { updateMe } = inject('me');
const hasBtnBack = computed(() => !!route.meta?.btnBack);

const logOut = async () => {
    await router.push({ name: ROUTE_NAMES.PAGE_DASHBOARD.routeName });
    const resp = await UserService.logOut();
    if (!resp) return;
    localStorage.removeItem('role');
    await updateMe({ login: '', userId: '', role: '' });
};

const countries = ref([]);
const getCountryList = async () => {
    const userLocale = Translations.guessDefaultLocale();
    const resp = await CatalogService.getCountryList();
    if (!resp) return;
    const names = new Intl.DisplayNames([`${userLocale}`], { type: 'region' });
    countries.value = resp.items.map(elem => {
        return { alpha2: elem.alpha2, value: names.of(elem.alpha2) };
    });
};
provide('countries', countries);

const currencies = ref([]);
const getCurrencyList = async () => {
    const resp = await CatalogService.getCurrenciesList();
    if (resp) currencies.value = resp.items;
};
provide('currencies', currencies);

const languages = ref([]);
const getLanguagesList = async () => {
    const resp = await LocalizationService.getLanguagesList();
    if (resp) languages.value = resp.items;
};
provide('languages', languages);

onMounted(() => {
    Promise.all([getLanguagesList(), getCurrencyList(), getCountryList()])
        .then(() => {
            isLoading.value = false;
        });

    emitter.on('change-languages', locale => {
        countries.value = changeLocaleCountries(countries.value, locale);
    });
});

onBeforeUnmount(() => {
    emitter.off('change-languages');
});
</script>

<style src="./styles.scss" lang='scss' scoped />
